import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, AtSignIcon, BarChart3Icon, BotIcon, BriefcaseIcon, Building2Icon, CalendarDaysIcon, CheckIcon, ChevronDownIcon, ImageIcon, InstagramIcon, LinkedinIcon, MapPinIcon, MegaphoneIcon, MessageCircleIcon, PhoneIcon, SendIcon, SparklesIcon, TargetIcon, UsersIcon, VideoIcon, YoutubeIcon } from 'lucide-react';
import { Form, LandingBlock, LandingBlockType, LandingPage } from '../../types';
import { blockDefinition } from '../../data/blockLibrary';
import { BrandLogo } from '../shared/BrandLogo';
import { Button } from '../ui/Button';
import { VideoReplay } from './VideoReplay';
import { DynamicForm, DynamicFormResult } from './DynamicForm';
const ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  Target: TargetIcon,
  Users: UsersIcon,
  Video: VideoIcon,
  Megaphone: MegaphoneIcon,
  Sparkles: SparklesIcon,
  Bot: BotIcon,
  BarChart3: BarChart3Icon,
  Building2: Building2Icon,
  CalendarDays: CalendarDaysIcon,
  MessageCircle: MessageCircleIcon,
  Briefcase: BriefcaseIcon
};
interface LandingRendererProps {
  landing: LandingPage;
  form?: Form;
  onSubmit?: (result: DynamicFormResult) => Promise<void>;
  preview?: boolean;
  /** Muestra los accesos de la plataforma en la barra superior (home de AVA). */
  authLinks?: boolean;
  /** Bloque resaltado en el constructor visual. */
  selectedBlockId?: string | null;
  /** Permite seleccionar un bloque haciendo clic sobre él en la vista previa. */
  onSelectBlock?: (blockId: string) => void;
}
const SPACING: Record<LandingBlock['spacing'], string> = {
  compacto: 'py-10 sm:py-14',
  normal: 'py-16 sm:py-24',
  amplio: 'py-24 sm:py-32'
};
function scrollToForm() {
  document.getElementById('formulario')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
}
function cfg<T>(block: LandingBlock, key: string, fallback: T): T {
  const value = block.config[key];
  return (value === undefined || value === null ? fallback : value) as T;
}

/** Interruptores del constructor: guardan «si» / «no» o un booleano. */
function flag(block: LandingBlock, key: string, fallback: boolean): boolean {
  const value = block.config[key];
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  return String(value) !== 'no';
}
function blockLabel(type: LandingBlockType): string {
  return blockDefinition(type).label;
}
const SHADOWS: Record<string, string> = {
  ninguna: 'none',
  suave: '0 4px 20px -6px rgba(11,31,77,0.10)',
  media: '0 14px 36px -12px rgba(11,31,77,0.20)',
  fuerte: '0 28px 64px -18px rgba(11,31,77,0.30)'
};
const BACKGROUNDS: Record<string, string | undefined> = {
  transparente: undefined,
  blanco: '#FFFFFF',
  gris: '#F5F7FA',
  marca: 'var(--primary)',
  oscuro: '#0B1F4D'
};

/** Traduce el estilo editable del bloque a CSS en línea. */
function wrapperStyle(block: LandingBlock): React.CSSProperties {
  const s = block.style;
  if (!s) return {};
  return {
    backgroundColor: s.background ? BACKGROUNDS[s.background] : undefined,
    color: s.textColor,
    paddingTop: s.paddingY !== undefined ? `${s.paddingY}px` : undefined,
    paddingBottom: s.paddingY !== undefined ? `${s.paddingY}px` : undefined,
    paddingLeft: s.paddingX !== undefined ? `${s.paddingX}px` : undefined,
    paddingRight: s.paddingX !== undefined ? `${s.paddingX}px` : undefined,
    borderRadius: s.radius !== undefined ? `${s.radius}px` : undefined,
    boxShadow: s.shadow ? SHADOWS[s.shadow] : undefined,
    textAlign: s.align,
    letterSpacing: s.letterSpacing !== undefined ? `${s.letterSpacing}px` : undefined,
    fontWeight: s.titleWeight,
    textTransform: s.uppercase ? 'uppercase' : undefined,
    overflow: s.radius ? 'hidden' : undefined
  };
}
const BUTTON_ANIMATION: Record<string, string> = {
  ninguna: '',
  elevar: 'hover:-translate-y-0.5',
  escalar: 'hover:scale-[1.03]',
  pulso: 'animate-pulse'
};
const BUTTON_ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  ArrowRight: ArrowRightIcon,
  Sparkles: SparklesIcon,
  Send: SendIcon,
  Calendar: CalendarDaysIcon,
  MessageCircle: MessageCircleIcon,
  Phone: PhoneIcon
};

/** Botón de un bloque, totalmente personalizable desde el constructor. */
function BlockCta({
  block,
  label,
  onClick
}: {
  block: LandingBlock;
  label: string;
  onClick: () => void;
}) {
  const [hover, setHover] = React.useState(false);
  const b = block.button ?? {};
  const Icon = b.icon ? BUTTON_ICONS[b.icon] : undefined;
  const style: React.CSSProperties = {
    backgroundColor: hover && b.hoverColor ? b.hoverColor : b.color,
    color: b.textColor,
    borderRadius: b.radius !== undefined ? `${b.radius}px` : undefined,
    height: b.height !== undefined ? `${b.height}px` : undefined,
    paddingLeft: b.paddingX !== undefined ? `${b.paddingX}px` : undefined,
    paddingRight: b.paddingX !== undefined ? `${b.paddingX}px` : undefined,
    width: b.fullWidth ? '100%' : undefined
  };
  return <Button size="lg" onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={style} className={`h-14 rounded-xl px-8 transition-transform duration-200 ${b.shadow === false ? '' : 'ava-glow'} ${BUTTON_ANIMATION[b.animation ?? 'elevar'] ?? ''}`} rightIcon={Icon ? <Icon className="h-4 w-4" data-id="element-351" data-mp="n4jwe66t63mq" /> : undefined} data-id="element-350" data-mp="15w2xu8l6p9mn">
      {b.label || label}
    </Button>;
}

/** Resalta en azul uno o varios fragmentos, separados por "|". */
function Highlighted({
  text,
  highlight
}: {
  text: string;
  highlight: string;
}) {
  const segments = highlight.split('|').map(s => s.trim()).filter(Boolean);
  if (segments.length === 0) return <>{text}</>;
  const escaped = segments.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const parts = text.split(new RegExp(`(${escaped.join('|')})`, 'g'));
  return <>
      {parts.map((part, index) => segments.includes(part) ? <span key={`${part}-${index}`} className="text-primary" data-id="element-352" data-mp="1bn5mvc4d5vjm">
            {part}
          </span> : <React.Fragment key={`t-${index}`} data-id="element-353" data-mp="1khkbi5asaife">{part}</React.Fragment>)}
    </>;
}

/** Anotación manuscrita con flecha apuntando al vídeo. */
function HandwrittenNote({
  text
}: {
  text: string;
}) {
  return <div className="mt-6 flex items-start justify-center gap-2 sm:gap-3" data-id="element-354" data-mp="spw3fuz1gi2d">
      <svg width="48" height="34" viewBox="0 0 48 34" fill="none" aria-hidden="true" className="mt-1 flex-none text-primary" data-id="element-355" data-mp="nrb0hnsejka8">
        <path d="M46 30C33 33 12 31 4 20C1.5 16.4 2.5 10 8 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" data-id="element-356" data-mp="11ozkktenane8" />
        <path d="M4 2.5L8.2 6.2L3.4 9.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-357" data-mp="2d4y3rq3jept6" />
      </svg>
      <p className="font-hand text-xl text-primary sm:text-2xl" data-id="element-358" data-mp="2gndououov7ot">{text}</p>
    </div>;
}
export function LandingRenderer({
  landing,
  form,
  onSubmit,
  preview = false,
  authLinks = false,
  selectedBlockId = null,
  onSelectBlock
}: LandingRendererProps) {
  const config = landing.pageConfig;
  const theme = config.theme;
  const surfaceClass = theme === 'claro_corporativo' ? 'bg-surface' : 'bg-background';
  const cardShadow = theme === 'minimalista' ? '' : 'shadow-soft';
  const blocks = config.blocks.filter(b => b.visible);

  /** Logotipo de la empresa, con altura configurable por bloque. */
  function mark(height = 30) {
    return <BrandLogo src={config.logoUrl} size={height} alt={landing.name} data-id="element-359" data-mp="2uny4xd97ypem" />;
  }
  const brandMark = mark();
  function renderBlock(block: LandingBlock) {
    switch (block.type) {
      case 'topbar':
        return <header key={block.id} className="sticky top-0 z-30 w-full border-b border-border/70 bg-background/80 backdrop-blur-xl" data-id="element-360" data-mp="3n4r1z5hndw7e">
            <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4 sm:px-8" data-id="element-361" data-mp="1n3u8hc96419j">
              {flag(block, 'showLogo', true) ? mark(Number(cfg(block, 'logoHeight', 30)) || 30) : <span data-id="element-362" data-mp="19be1k0d5bj3c" />}
              <div className="flex items-center gap-2 sm:gap-4" data-id="element-363" data-mp="19cfjmoskx5uk">
                {authLinks && <Link to="/login" className="hidden text-sm font-medium text-muted-foreground transition-colors hover:text-foreground sm:inline" data-id="element-364" data-mp="25yrbwlsapegd">
                    Iniciar sesión
                  </Link>}
                <Button size="sm" onClick={scrollToForm} className="rounded-full px-5" data-id="element-365" data-mp="1s0kk9ndxwap5">
                  {cfg(block, 'ctaLabel', 'Quiero información')}
                </Button>
              </div>
            </div>
          </header>;
      case 'logo':
        return <div key={block.id} className="flex justify-center px-5 pt-14" data-id="element-366" data-mp="2sabusc3m00oz">
            {mark(Number(cfg(block, 'logoHeight', 34)) || 34)}
          </div>;
      case 'hero':
        {
          const title = cfg(block, 'title', '');
          const eyebrow = cfg(block, 'eyebrow', '');
          return <section key={block.id} className="px-5 pb-6 pt-16 text-center sm:px-8 sm:pt-24" data-id="element-367" data-mp="361pcdkhyiohd">
            <div className="mx-auto max-w-4xl" data-id="element-368" data-mp="3aziiyen69ki6">
              {flag(block, 'showLogo', false) && <div className="mb-12 flex justify-center" data-id="element-369" data-mp="3fx0k3iuy6vfm">
                  {mark(Number(cfg(block, 'logoHeight', 34)) || 34)}
                </div>}
              {eyebrow && <span className="inline-flex items-center rounded-full border border-border bg-surface px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground" data-id="element-370" data-mp="3oyqkhe6t54pj">
                  {eyebrow}
                </span>}
              {cfg(block, 'brandLine', '') && <p className="mb-7 mt-5 text-[11px] font-semibold uppercase tracking-[0.28em] text-primary sm:text-xs" data-id="element-371" data-mp="2hzvauyvk8jc1">
                  {cfg(block, 'brandLine', '')}
                </p>}
              {!cfg(block, 'brandLine', '') && eyebrow && <div className="h-7" data-id="element-372" data-mp="2vxtmvxmes3od" />}
              <h1 className="mx-auto max-w-3xl text-[34px] font-semibold leading-[1.1] tracking-[-0.02em] text-navy sm:text-[56px]" data-id="element-373" data-mp="2vyuyc37c6s6m">
                <Highlighted text={title} highlight={cfg(block, 'highlight', '')} data-id="element-374" data-mp="34y87svvgngrc" />
              </h1>
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg" data-id="element-375" data-mp="39q9gk7tf6pcm">
                  {cfg(block, 'subtitle', '')}
                </p>}
              {cfg(block, 'extraLine', '') && <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground" data-id="element-376" data-mp="3om8onigj4phr">
                  {cfg(block, 'extraLine', '')}
                </p>}
            </div>
          </section>;
        }
      case 'titulo':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-377" data-mp="17y7przmmbar3">
            <h2 className="mx-auto max-w-3xl text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-378" data-mp="131qzayry4kau">
              {cfg(block, 'text', '')}
            </h2>
          </section>;
      case 'subtitulo':
        return <section key={block.id} className="px-5 pb-8" data-id="element-379" data-mp="1vr7ntlcg3h1s">
            <p className="mx-auto max-w-2xl text-center text-lg text-muted-foreground" data-id="element-380" data-mp="1qrlcjhg6gavm">
              {cfg(block, 'text', '')}
            </p>
          </section>;
      case 'imagen':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-381" data-mp="1gzxprtukfi0h">
            {cfg(block, 'url', '') ? <img src={cfg(block, 'url', '')} alt={cfg(block, 'alt', '')} className={`mx-auto w-full max-w-5xl rounded-2xl object-cover ${cardShadow}`} data-id="element-382" data-mp="2ehxpx7u6yztl" /> : <div className="mx-auto flex aspect-video w-full max-w-5xl items-center justify-center rounded-2xl border border-dashed border-border bg-surface text-sm text-muted-foreground" data-id="element-383" data-mp="cr70ytx9yiwj">
                Añade una imagen desde el editor
              </div>}
          </section>;
      case 'video':
        return <section key={block.id} className="px-5 pb-4 pt-6 sm:px-8 sm:pb-8" data-id="element-384" data-mp="3l4xl86zv150f">
            <VideoReplay url={cfg(block, 'url', '')} thumbnailUrl={cfg(block, 'thumbnailUrl', '')} overlayTitle={cfg(block, 'overlayTitle', '')} maxWidthClass="max-w-6xl" data-id="element-385" data-mp="3p958s0ul0ex2" />
            {cfg(block, 'note', '') && <HandwrittenNote text={cfg(block, 'note', '')} data-id="element-386" data-mp="19goh62tvvnch" />}
            {cfg(block, 'caption', '') && <p className="mx-auto mt-6 max-w-2xl text-center text-base text-muted-foreground" data-id="element-387" data-mp="1eeq0mov81ooh">
                {cfg(block, 'caption', '')}
              </p>}
            {cfg(block, 'ctaLabel', '') && <div className="mt-8 flex justify-center" data-id="element-388" data-mp="2u0tmfd2dzbju">
                <BlockCta block={block} label={cfg(block, 'ctaLabel', '')} onClick={scrollToForm} data-id="element-389" data-mp="38o4vn8b3ijgs" />
              </div>}
          </section>;
      case 'beneficios':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-390" data-mp="3rlpnmo95py5m">
            <div className="mx-auto max-w-6xl" data-id="element-391" data-mp="3mll1oiphcb80">
              {cfg(block, 'title', '') && <h2 className="mb-14 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-392" data-mp="1xau2ynkp6smh">
                  {cfg(block, 'title', '')}
                </h2>}
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-393" data-mp="jcgcmd76zcvb">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? SparklesIcon;
                  return <article key={`${item.title}-${index}`} className={`group rounded-2xl border border-border bg-background p-8 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 ${cardShadow}`} data-id="element-394" data-mp="t91guwbe0jy3" data-mpi={index}>
                      <span className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 transition-colors group-hover:bg-primary/15" data-id="element-395" data-mp="5j72297iclb7">
                        <Icon className="h-6 w-6 text-primary" data-id="element-396" data-mp="eeepnh6natek" />
                      </span>
                      <h3 className="mb-3 text-base font-semibold text-navy" data-id="element-397" data-mp="3e0boouirbgmj">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground" data-id="element-398" data-mp="3i0c4ojli7zvp">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'estadisticas':
        {
          const items = cfg<{
            value: string;
            label: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-399" data-mp="2rkbgat4ho88e">
            <div className="mx-auto grid max-w-4xl gap-10 text-center sm:grid-cols-3" data-id="element-400" data-mp="2rm60g57xrwbf">
              {items.map(item => <div key={item.label} data-id="element-401" data-mp="2hu8kkl5zgg6d">
                  <p className="text-4xl font-semibold tracking-tight text-primary sm:text-5xl" data-id="element-402" data-mp="2hv1lh5i9gbgl">
                    {item.value}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground" data-id="element-403" data-mp="28tk9nzbvyvxp">{item.label}</p>
                </div>)}
            </div>
          </section>;
        }
      case 'testimonios':
        {
          const items = cfg<{
            name: string;
            role: string;
            company: string;
            text: string;
            avatar?: string;
            result?: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-404" data-mp="1karwpvhc0xp2">
            <div className="mx-auto max-w-6xl" data-id="element-405" data-mp="2cxw4g87t7q4j">
              <h2 className="mb-14 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-406" data-mp="2cy4i42brazgs">
                {cfg(block, 'title', 'Testimonios')}
              </h2>
              <div className="grid gap-6 md:grid-cols-3" data-id="element-407" data-mp="2371kjai1d2m9">
                {items.map(item => <figure key={item.name} className={`flex flex-col rounded-2xl border border-border bg-background p-8 ${cardShadow}`} data-id="element-408" data-mp="285l8fgg6woh9">
                    {item.result && <span className="mb-5 inline-flex w-fit items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary" data-id="element-409" data-mp="30p9x6f9vhnq1">
                        {item.result}
                      </span>}
                    <blockquote className="flex-1 text-[15px] leading-relaxed text-foreground" data-id="element-410" data-mp="yyj903ks5au1">
                      “{item.text}”
                    </blockquote>
                    <figcaption className="mt-7 flex items-center gap-3 border-t border-border pt-6" data-id="element-411" data-mp="u1aqg1vpz4s9">
                      {item.avatar ? <img src={item.avatar} alt="" className="h-11 w-11 rounded-full object-cover" data-id="element-412" data-mp="1b2if2uwe0re" /> : <span className="flex h-11 w-11 items-center justify-center rounded-full bg-surface text-sm font-semibold text-muted-foreground" data-id="element-413" data-mp="b75g9wipvd8j">
                          {item.name.charAt(0)}
                        </span>}
                      <span className="min-w-0 flex-1 text-sm" data-id="element-414" data-mp="3g6un6cx8mdlh">
                        <span className="block truncate font-semibold text-foreground" data-id="element-415" data-mp="3g4hzdj4st90i">
                          {item.name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground" data-id="element-416" data-mp="3q3qp1zsms1h2">
                          {item.role}
                        </span>
                      </span>
                      <span className="hidden text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground sm:block" data-id="element-417" data-mp="2s7wofmtvt90">
                        {item.company}
                      </span>
                    </figcaption>
                  </figure>)}
              </div>
            </div>
          </section>;
        }
      case 'logos_clientes':
        {
          const items = cfg<string[]>(block, 'items', []);
          return <section key={block.id} className="px-5 py-14" data-id="element-418" data-mp="2ejbu50tsq62i">
            <p className="mb-8 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground" data-id="element-419" data-mp="2jgv7zzml0gtx">
              {cfg(block, 'title', '')}
            </p>
            <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-x-12 gap-y-6" data-id="element-420" data-mp="vtz5ycz9d9kc">
              {items.map(name => <span key={name} className="text-lg font-semibold tracking-tight text-muted-foreground/70" data-id="element-421" data-mp="rqjd7rqb7tcx">
                  {name}
                </span>)}
            </div>
          </section>;
        }
      case 'formulario':
        return <section key={block.id} id="formulario" className={`scroll-mt-24 px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-422" data-mp="1aj521s2sujo0">
            <div className="mx-auto mb-12 max-w-2xl text-center" data-id="element-423" data-mp="15jshxf3bbqp4">
              <h2 className="text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-424" data-mp="3v9mbpdp9zz5x">
                {cfg(block, 'title', 'Solicita información')}
              </h2>
              <p className="mt-4 text-base text-muted-foreground sm:text-lg" data-id="element-425" data-mp="5rqnzy048mrq">
                {cfg(block, 'subtitle', '')}
              </p>
            </div>
            {form ? <DynamicForm form={form} onSubmit={onSubmit} preview={preview} showProgress={cfg(block, 'showProgress', 'si') !== 'no'} hidePrivacyNotice={cfg(block, 'showPrivacy', 'si') === 'no'} data-id="element-426" data-mp="zflqohwkow0" /> : <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-border bg-surface p-12 text-center text-sm text-muted-foreground" data-id="element-427" data-mp="jrrz06a3hakx">
                Asocia un formulario a esta landing desde el editor.
              </div>}
          </section>;
      case 'faq':
        {
          const items = cfg<{
            q: string;
            a: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-428" data-mp="2ujt7ew3ty0fm">
            <div className="mx-auto max-w-3xl" data-id="element-429" data-mp="2um5vzs43f91n">
              <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-430" data-mp="1a2tpavkeb41c">
                {cfg(block, 'title', 'Preguntas frecuentes')}
              </h2>
              <div className="space-y-3" data-id="element-431" data-mp="1jzom9a10swdi">
                {items.map(item => <details key={item.q} className="group rounded-xl border border-border bg-background px-5 py-4 transition-colors hover:border-primary/30" data-id="element-432" data-mp="1or6pk4qin91n">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[15px] font-medium text-foreground marker:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-id="element-433" data-mp="1xqlbqrzngxgc">
                      {item.q}
                      <ChevronDownIcon className="h-4 w-4 flex-none text-muted-foreground transition-transform duration-200 group-open:rotate-180" aria-hidden="true" data-id="element-434" data-mp="8cewdpljpxra" />
                    </summary>
                    <p className="mt-4 text-sm leading-relaxed text-muted-foreground" data-id="element-435" data-mp="5b3p0x24d99w">
                      {item.a}
                    </p>
                  </details>)}
              </div>
            </div>
          </section>;
        }
      case 'calendario':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-436" data-mp="a8lps05zgi6t">
            <div className="mx-auto max-w-2xl rounded-2xl border border-border bg-background p-10 text-center shadow-soft" data-id="element-437" data-mp="k82ry7m10dy1">
              <CalendarDaysIcon className="mx-auto mb-5 h-8 w-8 text-primary" data-id="element-438" data-mp="xzr2w7kjgwdf" />
              <h2 className="text-2xl font-semibold tracking-tight text-navy" data-id="element-439" data-mp="t0oapnpnqkmv">
                {cfg(block, 'title', 'Agenda una llamada')}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground" data-id="element-440" data-mp="17xx56qa9c4it">
                {cfg(block, 'text', '')}
              </p>
              <div className="mt-8" data-id="element-441" data-mp="12wrcutkiej9t">
                <Button size="lg" onClick={() => {
                const url = cfg(block, 'url', '');
                if (url) window.open(url, '_blank', 'noopener');
              }} data-id="element-442" data-mp="1mk497xpo87ic">
                  Reservar hora
                </Button>
              </div>
            </div>
          </section>;
      case 'whatsapp':
        return <a key={block.id} href={`https://wa.me/${String(cfg(block, 'number', '')).replace(/\D/g, '')}?text=${encodeURIComponent(cfg(block, 'message', ''))}`} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-105" aria-label="Contactar por WhatsApp" data-id="element-443" data-mp="12vbvzy36kx0l">
            <MessageCircleIcon className="h-6 w-6" data-id="element-444" data-mp="ovkdbk1pysg9" />
          </a>;
      case 'cta_final':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-445" data-mp="1s681q4hsb3aj">
            <div className="mx-auto max-w-3xl text-center" data-id="element-446" data-mp="1nasnhbfv75dl">
              <h2 className="text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-447" data-mp="1igexxpnvvsgu">
                {cfg(block, 'title', '')}
              </h2>
              <p className="mt-4 text-lg text-muted-foreground" data-id="element-448" data-mp="18jk2jfmwq86s">
                {cfg(block, 'text', '')}
              </p>
              <div className="mt-10 flex justify-center" data-id="element-449" data-mp="14hp1mc8w1wtj">
                <BlockCta block={block} label={cfg(block, 'ctaLabel', 'Quiero empezar')} onClick={scrollToForm} data-id="element-450" data-mp="14kb9ye3lsgct" />
              </div>
            </div>
          </section>;
      case 'footer':
        return <footer key={block.id} className="border-t border-border px-5 py-12 sm:px-8" data-id="element-451" data-mp="2tusj2mr95tt6">
            <div className="mx-auto flex max-w-6xl flex-col items-center gap-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-452" data-mp="2whb12wii7ahi">
              <div className="flex flex-col items-center gap-4 sm:items-start" data-id="element-453" data-mp="31fmax78stkzq">
                {brandMark}
              </div>
              <div className="flex flex-col items-center gap-2 text-center text-xs text-muted-foreground" data-id="element-454" data-mp="367mh3hfgpa4r">
                <p data-id="element-455" data-mp="3b5flc4x7fu2e">{cfg(block, 'text', '')}</p>
                <div className="flex flex-wrap justify-center gap-x-5 gap-y-1" data-id="element-456" data-mp="2diyqr7fuvukc">
                  {cfg<string[]>(block, 'links', []).map(link => <span key={link} className="cursor-pointer hover:text-foreground" data-id="element-457" data-mp="2ihs5f36vprm3">
                      {link}
                    </span>)}
                </div>
              </div>
              <div className="flex gap-3 text-muted-foreground" data-id="element-458" data-mp="2sh0ubhmw0g1l">
                {[InstagramIcon, YoutubeIcon, LinkedinIcon].map((Icon, index) => <span key={index} className="flex h-9 w-9 items-center justify-center rounded-full border border-border transition-colors hover:border-primary/40 hover:text-primary" aria-hidden="true" data-id="element-459" data-mp="7opczje5h4xj" data-mpi={index}>
                    <Icon className="h-4 w-4" data-id="element-460" data-mp="2ct1ni9v7gfh2" />
                  </span>)}
              </div>
            </div>
          </footer>;
      case 'servicios':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-461" data-mp="1g4c60knhpovm">
            <div className="mx-auto max-w-6xl" data-id="element-462" data-mp="1tvhac01sz5ca">
              {cfg(block, 'title', '') && <h2 className="text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-463" data-mp="1p3h1fi3r923k">
                  {cfg(block, 'title', '')}
                </h2>}
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-4 max-w-2xl text-center text-base text-muted-foreground" data-id="element-464" data-mp="xcb04ycasycp">
                  {cfg(block, 'subtitle', '')}
                </p>}
              <div className={`grid gap-6 md:grid-cols-3 ${cfg(block, 'title', '') || cfg(block, 'subtitle', '') ? 'mt-14' : ''}`} data-id="element-465" data-mp="s04h84ycphko">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? BriefcaseIcon;
                  return <article key={`${item.title}-${index}`} className={`rounded-2xl border border-border bg-background p-8 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 ${cardShadow}`} data-id="element-466" data-mp="15x9mw3u5kefe" data-mpi={index}>
                      <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10" data-id="element-467" data-mp="10y6h2h6cm46d">
                        <Icon className="h-5 w-5 text-primary" data-id="element-468" data-mp="3gti5753c470q" />
                      </span>
                      <h3 className="mb-2 text-base font-semibold text-navy" data-id="element-469" data-mp="3lo5jyc8jdn24">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground" data-id="element-470" data-mp="2y0cz1p74ty3x">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'caracteristicas':
        {
          const items = cfg<{
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-471" data-mp="3gs5gqngr3wwc">
            <div className="mx-auto max-w-4xl" data-id="element-472" data-mp="3byay7ncvtnyu">
              <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-473" data-mp="37zloc4r1yojy">
                {cfg(block, 'title', 'Características')}
              </h2>
              <ul className="grid gap-x-10 gap-y-6 sm:grid-cols-2" data-id="element-474" data-mp="3k2el4sc87it">
                {items.map((item, index) => <li key={`${item.title}-${index}`} className="flex gap-3" data-id="element-475" data-mp="1m8dntd2qnxdz" data-mpi={index}>
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary/10" data-id="element-476" data-mp="1r5x0i8jsg23t">
                      <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-477" data-mp="1v6yzqpdpge5s" />
                    </span>
                    <span data-id="element-478" data-mp="20868aqno9hdi">
                      <span className="block text-[15px] font-medium text-foreground" data-id="element-479" data-mp="208epguqtv8ui">
                        {item.title}
                      </span>
                      <span className="block text-sm leading-relaxed text-muted-foreground" data-id="element-480" data-mp="2a4rj5smdhmp9">
                        {item.text}
                      </span>
                    </span>
                  </li>)}
              </ul>
            </div>
          </section>;
        }
      case 'galeria':
        {
          const items = cfg<string[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-481" data-mp="2sv94vd8sfi14">
            <div className="mx-auto max-w-6xl" data-id="element-482" data-mp="3t76kodo7j4f6">
              {cfg(block, 'title', '') && <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-483" data-mp="20lusx9w22jq">
                  {cfg(block, 'title', '')}
                </h2>}
              {items.length === 0 ? <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border bg-surface py-16 text-sm text-muted-foreground" data-id="element-484" data-mp="3p6cvzi2xxll8">
                  <ImageIcon className="h-6 w-6" aria-hidden="true" data-id="element-485" data-mp="kr3mqzmwcu41" />
                  Añade imágenes desde el editor
                </div> : <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-486" data-mp="6z6yj6ocn4cx">
                  {items.map((url, index) => <img key={`${url}-${index}`} src={url} alt="" className={`aspect-[4/3] w-full rounded-2xl object-cover ${cardShadow}`} data-id="element-487" data-mp="bwglmfo9n3xl" data-mpi={index} />)}
                </div>}
            </div>
          </section>;
        }
      case 'precios':
        {
          const items = cfg<{
            name: string;
            price: string;
            period: string;
            features: string;
            featured: string;
            ctaLabel: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-488" data-mp="uw398dkktd6k">
            <div className="mx-auto max-w-5xl" data-id="element-489" data-mp="qvje1fjkcjn2">
              <h2 className="text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-490" data-mp="14thpac2l7zyc">
                {cfg(block, 'title', 'Planes')}
              </h2>
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-4 max-w-2xl text-center text-base text-muted-foreground" data-id="element-491" data-mp="2g73chrovzrh8">
                  {cfg(block, 'subtitle', '')}
                </p>}
              <div className="mt-14 grid gap-6 md:grid-cols-2" data-id="element-492" data-mp="2jar9zko85wmr">
                {items.map((plan, index) => {
                  const featured = Boolean(plan.featured);
                  return <article key={`${plan.name}-${index}`} className={`flex flex-col rounded-2xl border bg-background p-8 ${featured ? 'border-primary shadow-soft' : 'border-border'}`} data-id="element-493" data-mp="2saefoinzlolm" data-mpi={index}>
                      {featured && <span className="mb-5 inline-flex w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary" data-id="element-494" data-mp="24lczuactrb6b">
                          Recomendado
                        </span>}
                      <h3 className="text-base font-semibold text-navy" data-id="element-495" data-mp="1gviojx85luo6">{plan.name}</h3>
                      <p className="mt-4 flex items-baseline gap-2" data-id="element-496" data-mp="1gtoddamopgxa">
                        <span className="text-4xl font-semibold tracking-tight text-navy" data-id="element-497" data-mp="1lt11rzt7ew1z">
                          {plan.price}
                        </span>
                        <span className="text-sm text-muted-foreground" data-id="element-498" data-mp="19bge9jnde0ow">
                          {plan.period}
                        </span>
                      </p>
                      <ul className="mt-7 flex-1 space-y-3" data-id="element-499" data-mp="14epzcznaxb52">
                        {String(plan.features ?? '').split('\n').filter(Boolean).map(feature => <li key={feature} className="flex gap-2.5 text-sm text-muted-foreground" data-id="element-500" data-mp="qkgwb2ealr54">
                              <CheckIcon className="mt-0.5 h-4 w-4 flex-none text-primary" data-id="element-501" data-mp="go3wrnw2gj2g" />
                              {feature}
                            </li>)}
                      </ul>
                      <Button className="mt-8 w-full" variant={featured ? 'primary' : 'secondary'} onClick={scrollToForm} data-id="element-502" data-mp="7nxja20dbj3a">
                        {plan.ctaLabel || 'Empezar'}
                      </Button>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'contacto':
        return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-503" data-mp="dmj22nfp1hpe">
            <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-background p-10 text-center shadow-soft" data-id="element-504" data-mp="imf0m75mggxk">
              <h2 className="text-2xl font-semibold tracking-tight text-navy sm:text-3xl" data-id="element-505" data-mp="4nfjrlocp6by">
                {cfg(block, 'title', 'Contacto')}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground" data-id="element-506" data-mp="1z0exf9967qgl">
                {cfg(block, 'text', '')}
              </p>
              <dl className="mt-8 grid gap-4 sm:grid-cols-3" data-id="element-507" data-mp="230pbzab13p72">
                {[{
                icon: AtSignIcon,
                value: cfg(block, 'email', '')
              }, {
                icon: PhoneIcon,
                value: cfg(block, 'phone', '')
              }, {
                icon: MapPinIcon,
                value: cfg(block, 'address', '')
              }].filter(entry => entry.value).map(entry => <div key={entry.value} className="flex flex-col items-center gap-2 rounded-xl bg-surface px-4 py-5" data-id="element-508" data-mp="3sht72qw5wy6t">
                      <entry.icon className="h-5 w-5 text-primary" aria-hidden="true" data-id="element-509" data-mp="642stcbn5a9i" />
                      <span className="break-words text-sm text-foreground" data-id="element-510" data-mp="k3kn277phfq9">
                        {entry.value}
                      </span>
                    </div>)}
              </dl>
            </div>
          </section>;
      default:
        return null;
    }
  }

  /** En el constructor cada bloque es seleccionable haciendo clic sobre él. */
  function renderEditable(block: LandingBlock) {
    const content = renderBlock(block);
    const style = wrapperStyle(block);
    const hasStyle = Object.values(style).some(v => v !== undefined);
    if (!onSelectBlock) {
      return hasStyle ? <div key={block.id} style={style} data-id="element-511" data-mp="upks29jaqrfy">
          {content}
        </div> : content;
    }
    const selected = selectedBlockId === block.id;
    return <div key={block.id} role="button" tabIndex={0} onClick={() => onSelectBlock(block.id)} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelectBlock(block.id);
      }
    }} style={style} className={`relative cursor-pointer outline-none transition-shadow ${selected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : 'hover:ring-2 hover:ring-primary/30'}`} data-id="element-512" data-mp="13rtzgqj9q4p4">
        {selected && <span className="pointer-events-none absolute left-3 top-3 z-40 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground" data-id="element-513" data-mp="3bz6ypmjr1fj7">
            {blockLabel(block.type)}
          </span>}
        {content}
      </div>;
  }
  return <div className="w-full bg-background" style={{
    '--primary': config.primaryColor,
    fontFamily: `${config.fontFamily}, Inter, sans-serif`
  } as React.CSSProperties} data-id="element-514" data-mp="32ztylkhim3b5">
      {blocks.map(renderEditable)}
      {blocks.length === 0 && <div className="flex min-h-[300px] items-center justify-center p-10 text-center text-sm text-muted-foreground" data-id="element-515" data-mp="3qpxs2z0zomof">
          Esta landing no tiene bloques visibles. Activa alguno desde el editor.
        </div>}
    </div>;
}